/**
 * Automations capability / skill catalog + toolset derivation (BAL-8506 / BAL-8361 Wave 0).
 *
 * Customers author automations by selecting CAPABILITIES (an ability) and SKILLS (a procedure) — never
 * raw tools. The server derives the dependency-closed `agent_config.tools` and a `maxSteps` cap from the
 * selection, so the BAL-8180 broken state (a tool without its prerequisite) is UNREPRESENTABLE rather
 * than merely validated against. Grounded in the ACTUAL tool vocabulary (`WORKFORCE_AGENT_TOOL_NAMES`) —
 * the demo's video/phone/vendor cards have no runtime tools and are not modeled here.
 */

import {
  WORKFORCE_SKILL_DELIVERY_TOOL,
  WORKFORCE_SKILL_REQUIRED_TOOLS,
} from './workforce-agent-skill-tools';
import { resolveWorkforceToolClosure } from './workforce-agent-tool-deps';
import { WORKFORCE_AGENT_SKILL_KEYS } from './workforce-agents';
import type { WorkforceAgentSkillKey, WorkforceAgentToolName } from './workforce-agents';

/** Always-on tools every agentic definition gets — not user-facing capabilities. */
export const WORKFORCE_BASELINE_AGENT_TOOLS = [
  'read_task',
  'read_report',
  'list_evidence',
] as const satisfies readonly WorkforceAgentToolName[];

export type WorkforceCapabilityKey =
  | 'web-search'
  | 'browse-websites'
  | 'screenshots'
  | 'document-collection'
  | 'structured-field-extraction'
  | 'site-infrastructure-checks';

export type WorkforceCapabilityDef = {
  label: string;
  description: string;
  /** The `WORKFORCE_AGENT_TOOL_NAMES` this capability expands to. */
  produces: readonly WorkforceAgentToolName[];
  /** OTHER capability keys this one implies (transparency line; the closure adds the tools regardless). */
  reliesOn: readonly WorkforceCapabilityKey[];
  /** Per-entry contribution to the derived `maxSteps` cap (see the derivation constants below). */
  stepWeight: number;
};

export const WORKFORCE_CAPABILITY_CATALOG = {
  'web-search': {
    label: 'Web search',
    description: 'Search the web for sources about the merchant.',
    produces: ['serp'],
    reliesOn: [],
    stepWeight: 5,
  },
  'browse-websites': {
    label: 'Browse websites',
    description: 'Open and navigate web pages, following links and interacting with the page.',
    produces: ['browser', 'browser_act', 'http_fetch'],
    reliesOn: [],
    stepWeight: 15,
  },
  screenshots: {
    label: 'Screenshots',
    description: 'Capture and read screenshots of the pages the agent visits as evidence.',
    produces: ['screenshot', 'inspect_image'],
    // screenshot needs a page open (browser); the closure adds it — this is the transparency line.
    reliesOn: ['browse-websites'],
    stepWeight: 8,
  },
  'document-collection': {
    label: 'Document collection',
    description: 'Fetch and store documents (PDFs, filings) as evidence.',
    produces: ['store_document', 'http_fetch'],
    reliesOn: [],
    stepWeight: 8,
  },
  'structured-field-extraction': {
    label: 'Structured field extraction',
    description: 'Extract structured fields from a page and compare them to declared details.',
    produces: ['extract_fields'],
    reliesOn: [],
    stepWeight: 5,
  },
  'site-infrastructure-checks': {
    label: 'Site infrastructure checks',
    description: 'Inspect a site’s HTTP responses and TLS certificate.',
    produces: ['http_request', 'tls_inspect'],
    reliesOn: [],
    stepWeight: 5,
  },
} as const satisfies Record<WorkforceCapabilityKey, WorkforceCapabilityDef>;

export const WORKFORCE_CAPABILITY_KEYS = Object.keys(
  WORKFORCE_CAPABILITY_CATALOG,
) as WorkforceCapabilityKey[];

export type WorkforceSkillCardDef = {
  label: string;
  description: string;
  /** Capability keys the skill's required tools map back to (transparency; not auto-selected). */
  reliesOn: readonly WorkforceCapabilityKey[];
  stepWeight: number;
};

export const WORKFORCE_SKILL_CATALOG = {
  'uk-fca-register': {
    label: 'Check the UK FCA register',
    description: 'Look up the merchant on the UK Financial Conduct Authority register.',
    // fca_register + load_skill ride along invisibly; screenshot maps back to the Screenshots capability.
    reliesOn: ['screenshots'],
    stepWeight: 10,
  },
} as const satisfies Record<WorkforceAgentSkillKey, WorkforceSkillCardDef>;

// maxSteps derivation constants — FIRST-CUT, product-tunable (see the plan-doc pending ledger). These
// are kill-switches sized so legitimate runs don't hit them (ONC-134), never budgets.
export const WORKFORCE_MAX_STEPS_BASE = 10;
export const WORKFORCE_MAX_STEPS_CEILING = 60;

/**
 * The dependency-closed `agent_config.tools` for a selection: capability tools ∪ skill required tools ∪
 * `load_skill` (when any skill is selected) ∪ baseline, run through `resolveWorkforceToolClosure` so a
 * tool can never appear without its prerequisite.
 */
export const deriveWorkforceAgentTools = (
  capabilities: readonly WorkforceCapabilityKey[],
  skills: readonly WorkforceAgentSkillKey[],
): string[] => {
  const capabilityTools = capabilities.flatMap(key => WORKFORCE_CAPABILITY_CATALOG[key].produces);
  const skillTools = skills.flatMap(key => WORKFORCE_SKILL_REQUIRED_TOOLS[key]);
  const deliveryTool = skills.length > 0 ? [WORKFORCE_SKILL_DELIVERY_TOOL] : [];

  return resolveWorkforceToolClosure([
    ...WORKFORCE_BASELINE_AGENT_TOOLS,
    ...capabilityTools,
    ...skillTools,
    ...deliveryTool,
  ]);
};

/** The derived `maxSteps` cap: base + Σ per-entry step weights, clamped to the global ceiling. */
export const deriveWorkforceMaxSteps = (
  capabilities: readonly WorkforceCapabilityKey[],
  skills: readonly WorkforceAgentSkillKey[],
): number => {
  const total =
    WORKFORCE_MAX_STEPS_BASE +
    capabilities.reduce((sum, key) => sum + WORKFORCE_CAPABILITY_CATALOG[key].stepWeight, 0) +
    skills.reduce((sum, key) => sum + WORKFORCE_SKILL_CATALOG[key].stepWeight, 0);

  return Math.min(total, WORKFORCE_MAX_STEPS_CEILING);
};

/** The skill keys the catalog covers — pinned to the vocabulary so a new skill can't be forgotten here. */
export const WORKFORCE_SKILL_CATALOG_KEYS = WORKFORCE_AGENT_SKILL_KEYS;
